import type { ReactNode } from 'react'
import { Select, SelectContent, SelectItem, SelectTrigger } from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { DASH } from '@/ui/format'

// 고르는 칸 하나 — 사이트의 모양으로 선 드롭다운(F-110). 키보드·터치·스크린 리더는 Radix Select 가 맡는다.
//
// 닫힌 모양은 둘이다. 기본은 카드다(아이콘 · 제목 · 보조 줄 · 꺾쇠) — 이미지·사양·GPU 모델·노드처럼 고른 것의 사실이
// 함께 서야 하는 자리. `compact` 는 한 줄이다 — 장 수·디스크 크기처럼 값이 곧 사실인 자리.
// 열린 목록의 항목은 제목과 보조 줄 두 줄이고 고른 항목에 체크가 선다. **잠긴 항목은 빼지 않는다** — 흐리게 서고
// 사유(`메모리 부족` 같은)가 제목 옆에 붙는다. 목록에서 빼면 사람은 그 항목이 왜 없는지 모른다.
//
// `id` 는 트리거(button)에 붙어 `Label htmlFor` 가 그대로 이어지고, 막힘 때 그 id 로 포커스가 간다.
// 빈 값('')도 항목이 될 수 있다(`없음`·`자동`) — Radix 는 ''를 값으로 받지 않아 안에서만 다른 표식으로 바꾼다.
// Radix 가 내는 날것의 ''는 버린다 — 값과 항목이 한 번에 바뀌면 숨은 native select 가 새 option 이 없는 순간 ''를 내고,
// 그것을 넘기면 고른 값이 빈다. 사람이 고른 빈 항목은 위의 표식으로 오므로 ''가 될 수 없다.
// 고른 값은 트리거의 `data-value` 에도 선다 — 화면 밖(테스트)에서 지금 값을 읽는 자리다.
// 붙일 `Label` 이 없는 자리(목록 위 도구 줄의 거르개)는 `label` 이 트리거의 이름이 된다.

export type ChoiceItem = {
  value: string
  title: string
  /** 항목의 두 번째 줄. compact 의 닫힌 모양에는 서지 않는다. */
  detail?: string | undefined
  disabled?: boolean | undefined
  /** 잠긴 이유 — 제목 옆에 붙는다. */
  reason?: string | undefined
}

const EMPTY = '\u0000'
const inner = (v: string) => (v === '' ? EMPTY : v)
const outer = (v: string) => (v === EMPTY ? '' : v)

export function Choice({
  id,
  value,
  items,
  onChange,
  icon,
  invalid = false,
  compact = false,
  disabled = false,
  label,
}: {
  id: string
  value: string
  items: ChoiceItem[]
  onChange: (value: string) => void
  icon?: ReactNode
  invalid?: boolean
  compact?: boolean
  disabled?: boolean
  label?: string
}) {
  const chosen = items.find((i) => i.value === value)
  return (
    <Select value={inner(value)} onValueChange={(v) => v !== '' && onChange(outer(v))} disabled={disabled}>
      <SelectTrigger
        id={id}
        data-value={value}
        aria-label={label}
        aria-invalid={invalid || undefined}
        className={cn(
          // 글자가 16px 미만이면 포커스 때 화면이 확대된다 — 좁은 화면에서만 키운다.
          'w-full min-w-0 bg-background text-base shadow-none sm:text-[13px]',
          // 트리거의 높이는 `data-[size=default]:h-9` 다 — 그냥 `h-auto` 는 특정성에서 지고 tailwind-merge 도 지우지 않아
          // 두 줄이 36px 에 묶여 넘친다. 높이는 같은 변형으로 덮는다(jsdom 은 배치를 재지 않아 테스트가 보지 못한다).
          compact ? 'h-9' : 'py-2 pl-3 whitespace-normal data-[size=default]:h-auto',
        )}
      >
        {compact ? (
          <span className="min-w-0 truncate">{chosen?.title ?? DASH}</span>
        ) : (
          <span className="flex min-w-0 items-center gap-2.5 text-left">
            {icon}
            <span className="min-w-0">
              <span className="block font-medium break-words">{chosen?.title ?? DASH}</span>
              {chosen?.detail ? <span className="block text-[12px] break-words text-muted-foreground">{chosen.detail}</span> : null}
            </span>
          </span>
        )}
      </SelectTrigger>
      <SelectContent position="popper" className="max-w-[calc(100vw-2rem)] min-w-(--radix-select-trigger-width)">
        {items.map((i) => (
          <SelectItem key={i.value} value={inner(i.value)} disabled={i.disabled ?? false} textValue={i.title} data-value={i.value}>
            <span className="grid min-w-0 gap-0.5">
              <span className="break-words">
                {i.title}
                {i.reason ? <span className="text-muted-foreground"> · {i.reason}</span> : null}
              </span>
              {i.detail ? <span className="text-[12px] break-words text-muted-foreground">{i.detail}</span> : null}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
